/**
 * Mission types
 */

export enum MissionStatus {
  PENDING = 'Pending', // Pending / Draft - Mission information not fully completed
  PENDING_ADMIN_REVIEW = 'PendingAdminReview', // Payment authorized, waiting for admin review
  SUBMITTED = 'Submitted', // Approved by Admin / Connecting with Ghost - Mission submitted, no Ghost yet
  GHOST_ASSIGNED = 'GhostAssigned',
  MISSION_IN_PROGRESS = 'MissionInProgress', // Start date & time reached
  COMPLETED = 'Completed', // Ghost submits debrief + evidence
  UNDER_REVIEW = 'UnderReview', // Admin review required after flag
  CLOSED = 'Closed', // Review/dispute final state
  CANCELLED = 'Cancelled', // From Admin or Asset
  EXPIRED = 'Expired',
}

export type ReviewDecision = 'Approved' | 'Rejected';

export type MissionType = 'QuickHit' | 'LightSurveillance' | 'FullInvestigative';

export type GhostTier = 'Junior' | 'Senior' | 'Elite';

export interface MissionLocation {
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

export interface MissionTimeframe {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  deadline?: string;
}

export interface MissionMessage {
  id: string;
  conversationId: string;
  senderUserId: string;
  senderRole: 'Asset' | 'Ghost' | 'Admin' | 'System';
  messageType: 'Text' | 'Image' | 'File';
  content: string;
  createdAt: string;
}

export interface PaginatedMissionMessages {
  total: number;
  items: MissionMessage[];
}

export interface MissionAssignee {
  id: string;
  name: string;
  avatar?: string;
}

export interface MissionStatusHistory {
  status: string;
  timestamp: Date;
  timestampText: string;
  description?: string;
  userId?: string;
  userName?: string;
}

export interface MissionEvidence {
  id: string;
  type: 'photo' | 'video' | 'file';
  url: string;
  thumbnailUrl?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  uploadedAt: Date;
  uploadedAtText: string;
  uploadedBy?: string;
}

export interface MissionAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
  uploadedAt: Date;
  uploadedAtText: string;
  uploadedBy?: string;
}

export interface MissionReport {
  id: string;
  content: string;
  summary?: string;
  createdAt: Date;
  createdAtText: string;
  createdBy?: string;
  downloadUrl?: string;
}

export interface MissionActivity {
  id: string;
  type: 'created' | 'updated' | 'status_changed' | 'file_uploaded' | 'comment' | 'assigned';
  description: string;
  timestamp: Date;
  timestampText: string;
  userId?: string;
  userName?: string;
  metadata?: Record<string, any>;
}

export interface Mission {
  id: string;
  code: string;
  title: string;
  missionType: MissionType;
  category: string;
  ghostTier?: GhostTier | number;
  status: MissionStatus;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
  assetId: string;
  assetFirstName?: string;
  assetLastName?: string;
  assetCallSign?: string;
  assetEmail?: string;
  ghostId?: string;
  ghostFirstName?: string;
  ghostLastName?: string;
  ghostCallSign?: string;
  ghostEmail?: string;
  candidateGhostId?: string;
  candidateGhostFirstName?: string;
  candidateGhostLastName?: string;
  candidateGhostCallSign?: string;
  candidateGhostEmail?: string;
}

export interface MissionDetail extends Mission {
  // Basic Info
  description?: string;
  createdText?: string;
  lastUpdatedText?: string;

  // Location & Timeframe
  location?: MissionLocation;
  timeframe?: MissionTimeframe;

  // User Info
  assetName?: string;
  ghostName?: string;
  candidateGhostName?: string;

  // Category & Questions
  categoryId?: string;
  subcategoryId?: string | null;
  categoryName?: string;
  subcategoryName?: string;
  categoryAnswers?: Record<string, any>;

  // Assignee
  assignee?: MissionAssignee;

  // Evidence & Attachments
  evidence?: MissionEvidence[];
  attachments?: MissionAttachment[];

  // Messages
  messages?: MissionMessage[];

  // Report
  report?: MissionReport;

  // Status History
  statusHistory?: MissionStatusHistory[];

  // Activity History
  activities?: MissionActivity[];

  // Pricing (optional)
  price?: number;
  basePrice?: number;
  missionTypeMultiplier?: number;
  ghostTierMultiplier?: number;
  assetBookingFee?: number;
  platformFee?: number;
  totalPrice?: number;
  ghostEarnings?: number;

  // Other
  estimatedCompletionHours?: number;
  confidence?: number;

  // Report sections
  reportWeThink?: string;
  reportConfidence?: number;
  reportHereWhy?: string;

  // Admin review
  reviewDecision?: ReviewDecision | null;
  reviewReason?: string | null;
  cancellationReason?: string | null;
  canApproveReview?: boolean;
  canRejectReview?: boolean;
  latestPaymentStatus?: string | null;
  cancellationReason?: string | null;
  cancelledAtUtc?: string | null;
  cancelledAtText?: string | null;
  cancelledBy?: string | null;
  refundDecision?: RefundDecision | null;
  refundAmount?: number | null;
}

export enum RefundDecision {
  None = 'None',
  Full = 'Full',
  Partial = 'Partial',
  Denied = 'Denied'
}

export interface MissionFilter {
  status?: MissionStatus | MissionStatus[] | 'all';
  search?: string;
  userId?: string;
  dateRange?: {
    start?: Date;
    end?: Date;
  };
}

export interface PaginatedMissions {
  data: Mission[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
